/**
 * Per-line payment helpers for extra-item links (ported from the RBKC
 * my-ceremonies pattern). Link `data.cost` is a per-unit price and
 * `data.paid` a line total, both in POUNDS — unlike `purchase.price` /
 * `purchase.paid`, which are pence. `paid` is stamped onto links by the
 * bl-hey-centric callback at booking time (RWRSPI-1026) and by this app's
 * payment-verify settlement after a balance payment; links created before
 * either exist have no `paid` at all, so every reader must coerce.
 */

export function round2(value: number): number {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/** Full line total = cost × quantity, in pounds. */
export function lineTotal(data: any): number {
  const cost = Number(data?.cost) || 0;
  const quantity = Number(data?.quantity) || 1;
  return round2(cost * quantity);
}

/** Unpaid remainder of a line, floored at zero, in pounds. */
export function lineOutstanding(data: any): number {
  const paid = Number(data?.paid) || 0;
  return round2(Math.max(lineTotal(data) - paid, 0));
}

/** Sum of unpaid remainders across a booking's links, in pounds. */
export function linksOutstanding(links: any[]): number {
  return round2(
    (links ?? []).reduce((sum, l) => sum + lineOutstanding(l?.data), 0),
  );
}

/**
 * Overpayment on a line — the mirror of lineOutstanding, floored at zero, in
 * pounds. Non-zero when `paid` exceeds cost × quantity, which happens when a
 * booking is rescheduled DOWNWARD (cheaper season, day or room): the customer
 * has already paid the old, higher price and is owed the difference.
 *
 * lineOutstanding floors at zero, so a credit is invisible to it — these two
 * are not interchangeable and a caller wanting "how far off settled is this
 * line, in either direction" needs both.
 */
export function lineCredit(data: any): number {
  const paid = Number(data?.paid) || 0;
  return round2(Math.max(paid - lineTotal(data), 0));
}

/** Sum of overpayments across a booking's links, in pounds. Refund owed. */
export function linksCredit(links: any[]): number {
  return round2((links ?? []).reduce((sum, l) => sum + lineCredit(l?.data), 0));
}

/**
 * Whether the booking's deposit has been attributed to a line. RWRSPI-1026
 * stamps a numeric `paid` on EVERY fee line at booking time (the deposit on
 * the booking-fee line, 0 elsewhere — earlier 1026 bookings carried it on
 * the ceremony-fee line instead), so the ceremony-fee line
 * (`questionid === 'noid2'`) carrying a numeric `paid` is the signal.
 * Checking that line specifically — rather than any stamp anywhere — matters
 * because the add-ons/extend/reschedule flows stamp OTHER links; a pre-1026
 * booking that buys an add-on must not flip onto the per-line model with its
 * deposit still unattributed (it would be re-charged).
 */
export function depositAttributed(links: any[]): boolean {
  return (links ?? []).some(
    (l) =>
      l?.data?.questionid === 'noid2' &&
      l?.data?.paid != null &&
      !Number.isNaN(Number(l.data.paid)),
  );
}

/**
 * Greedily attribute an already-paid total (from the purchase-payments
 * history — the reliable record on Westminster, where purchase.paid is
 * never rolled up) across a booking's links, returning COPIES whose
 * `data.paid` reflects the attribution. Nothing is written to the API; the
 * copies feed the per-line gateway amounts, and full settlement after the
 * payment stamps the real links. Attribution order: the £100 deposit-style
 * line first (the line the legacy code excluded outright), then the
 * ceremony-fee line (`questionid === 'noid2'`), then the rest in order.
 */
export function attributePaidAcrossLinks(links: any[], paidTotal: number): any[] {
  // Payments that stamped lines directly (add-ons, extend, reschedule) also
  // created purchase-payment records, so the already-stamped total must come
  // off the history total before attributing — otherwise those payments
  // count twice and the outstanding balance under-reports.
  const alreadyStamped = round2(
    (links ?? []).reduce((sum, l) => sum + (Number(l?.data?.paid) || 0), 0),
  );
  let remaining = round2(
    Math.max((Number(paidTotal) || 0) - alreadyStamped, 0),
  );
  const priority = (l: any) =>
    l?.data?.cost === 100 ? 0 : l?.data?.questionid === 'noid2' ? 1 : 2;
  const ordered = [...(links ?? [])].sort((a, b) => priority(a) - priority(b));
  const attributedPaid = new Map<any, number>();
  for (const link of ordered) {
    if (remaining <= 0) break;
    const existing = Number(link?.data?.paid) || 0;
    const headroom = round2(lineTotal(link?.data) - existing);
    const attributable = Math.min(remaining, Math.max(headroom, 0));
    if (attributable > 0) {
      attributedPaid.set(link, round2(existing + attributable));
      remaining = round2(remaining - attributable);
    }
  }
  if (remaining > 0) {
    // The payment history exceeds the sum of every line total, so the customer
    // is in credit — usually a booking rescheduled to something cheaper. The
    // excess is deliberately NOT attributed here (that would make a line look
    // overpaid on the strength of an attribution guess rather than a recorded
    // payment), but it must not vanish silently: a refund is owed.
    console.warn(
      `attributePaidAcrossLinks: £${remaining} of recorded payment exceeds the booking's line totals — customer is in credit and a refund is likely owed`,
      { paidTotal, alreadyStamped },
    );
  }
  return (links ?? []).map((l) =>
    attributedPaid.has(l) ? { ...l, data: { ...l.data, paid: attributedPaid.get(l) } } : l,
  );
}
